<template>
  <master-layout :pageTitle="$t('home')">
    <div class="home-intro ion-padding">
      <div class="home-intro__content" v-if="loading">
        <p>{{ $t('loading') }}</p>
      </div>
      <div class="home-intro__content" v-else-if="error">
        <p>{{ error }}</p>
      </div>
      <div class="home-intro__content" v-else>
        <div class="doc-text" v-html="content"></div>
      </div>
    </div>

    <ion-grid>
      <ion-row v-for="(row, rowIndex) in menuRows" :key="rowIndex">
        <ion-col
          v-for="item in row"
          :key="item.labelKey"
          class="ion-text-center"
        >
          <ion-button size="large" shape="round" @click="linkroute(item.route)">
            <ion-icon :icon="item.icon"></ion-icon>
          </ion-button>
          <br />
          <ion-label>{{ $t(item.labelKey) }}</ion-label>
        </ion-col>
      </ion-row>
    </ion-grid>
  </master-layout>
</template>

<script>
import { IonGrid, IonRow, IonCol, IonButton, IonLabel, IonIcon } from '@ionic/vue';
import { defineComponent } from 'vue';
import { book, documentText, list, logIn, musicalNote, musicalNotes, newspaper, people, peopleCircle, person, personCircle, personAdd, settings } from 'ionicons/icons';

export default defineComponent({
  name: 'HomePage',
  components: {
    IonGrid,
    IonRow,
    IonCol,
    IonButton,
    IonLabel,
    IonIcon
  },
  data() {
    return {
      content: '',
      loading: false,
      error: '',
      menuItems: [
        { labelKey: 'tunes', routeWhenLoggedIn: '/usertunes', routeWhenLoggedOut: '/tunes', icon: musicalNote, public: true },
        { labelKey: 'tunebooks', route: '/tunebooks', icon: book, public: true },
        { labelKey: 'musicians', route: '/musicians', icon: personCircle, public: true },
        { labelKey: 'documentation', route: '/documentation', icon: documentText, public: true },
        { labelKey: 'groups', route: '/groups', icon: peopleCircle, public: false },
        { labelKey: 'users', route: '/users', icon: people, public: false },
        { labelKey: 'playlists', route: '/playlists', icon: list, public: false },
        { labelKey: 'sessions', route: '/sessions', icon: musicalNotes, public: false },
        { labelKey: 'profile', route: '/profile', icon: person, public: false },
        { labelKey: 'messages', route: '/messages', icon: newspaper, public: false },
        { labelKey: 'settings', route: '/settings', icon: settings, public: false },
        { labelKey: 'login', route: '/login', icon: logIn, public: true, guestsOnly: true },
        { labelKey: 'signup', route: '/register', icon: personAdd, public: true, guestsOnly: true },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    loggedIn() {
      return !!this.currentUser;
    },
    visibleMenuItems() {
      return this.menuItems
        .filter((item) => {
          if (item.guestsOnly) {
            return !this.loggedIn;
          }
          return this.loggedIn || item.public;
        })
        .map((item) => ({
          ...item,
          route: item.route || (this.loggedIn ? item.routeWhenLoggedIn : item.routeWhenLoggedOut),
        }));
    },
    menuRows() {
      const rows = [];
      for (let i = 0; i < this.visibleMenuItems.length; i += 3) {
        rows.push(this.visibleMenuItems.slice(i, i + 3));
      }
      return rows;
    },
  },
  watch: {
    '$i18n.locale'() {
      this.loadHomeIntro();
    }
  },
  mounted() {
    this.loadHomeIntro();
  },
  methods: {
    buildHomeTextUrl(locale) {
      const lang = locale || 'en';
      return `/docs/${lang}/home-intro.txt`;
    },
    async loadHomeIntro() {
      this.loading = true;
      this.error = '';
      this.content = '';

      const locale = this.$i18n?.locale || 'en';
      const primaryUrl = this.buildHomeTextUrl(locale);
      const fallbackUrl = locale === 'en' ? null : this.buildHomeTextUrl('en');

      try {
        let response = await fetch(primaryUrl);
        if (!response.ok && fallbackUrl) {
          response = await fetch(fallbackUrl);
        }
        if (!response.ok) {
          throw new Error('Failed to load home introduction');
        }

        this.content = await response.text();
      } catch (e) {
        console.error(e);
        this.error = 'Unable to load home page text.';
      } finally {
        this.loading = false;
      }
    },
    linkroute(page) {
      this.$router.push(page);
    }
  }
});
</script>

<style scoped>
.home-intro {
  padding-bottom: 0;
}

.home-intro__content {
  white-space: pre-wrap;
  background: var(--ion-color-light);
  border: 1px solid var(--ion-color-light-shade, #d7d8da);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.doc-text {
  white-space: pre-wrap;
  font-family: inherit;
}
</style>
